import React, { useEffect } from 'react';
import { mockData } from '../data/mockData';
import { GitHubIcon, LinkedInIcon, TwitterIcon, MediumIcon } from './Icons';
import useAnalytics from '../hooks/useAnalytics';

const HeroSection = () => {
    const { trackPageView, trackCVDownload, trackContactInteraction, trackSocialClick } = useAnalytics();

    useEffect(() => {
        trackPageView('hero_section');
    }, [trackPageView]);

    const handleCVDownload = () => {
        trackCVDownload();
        const link = document.createElement('a');
        link.href = '/images/resume/Manav_Gupta_Resume.pdf';
        link.download = 'Manav_Gupta_Resume.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleEmailClick = () => {
        trackContactInteraction('email', mockData.personalInfo.email);
    };

    const handleSocialClick = (platform, url) => {
        trackSocialClick(platform, url);
    };

    const socialLinks = [
        { platform: 'github', icon: GitHubIcon, url: mockData.personalInfo.socialLinks.github },
        { platform: 'linkedin', icon: LinkedInIcon, url: mockData.personalInfo.socialLinks.linkedin },
        { platform: 'twitter', icon: TwitterIcon, url: mockData.personalInfo.socialLinks.twitter },
        { platform: 'medium', icon: MediumIcon, url: mockData.personalInfo.socialLinks.medium },
    ];

    return (
        <section className="relative min-h-screen flex flex-col justify-center px-6 pt-24 pb-16 overflow-hidden">
            {/* Background Gradient Blobs */}
            <div className="absolute top-20 right-0 w-[500px] h-[500px] gradient-blob gradient-blob-coral animate-float-slow opacity-40" />
            <div className="absolute bottom-20 left-0 w-[400px] h-[400px] gradient-blob gradient-blob-peach animate-float-slower opacity-50" />

            <div className="max-w-4xl mx-auto w-full text-center relative z-10">
                {/* Label */}
                <div className="mb-6 animate-fadeInUp">
                    <span className="label-text inline-flex items-center gap-2">
                        <span className="w-2 h-2 bg-coral-500 rounded-full" />
                        Software Engineer
                    </span>
                </div>

                {/* Main Headline */}
                <h1 className="text-hero text-charcoal-900 mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                    Building products,<br />
                    <em className="italic-emphasis">one feature</em> at a time.
                </h1>

                {/* Subtitle */}
                <p className="text-lg md:text-xl text-charcoal-600 max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                    I'm {mockData.personalInfo.name}, a software engineer who builds scalable mobile and web apps with Flutter, React, and modern tech. I care about clean code and great user experiences.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                    <a
                        href={`mailto:${mockData.personalInfo.email}`}
                        onClick={handleEmailClick}
                        className="btn-primary"
                    >
                        Let's Build Something
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </a>
                    <button
                        onClick={handleCVDownload}
                        className="btn-secondary"
                    >
                        Download CV
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                    </button>
                </div>

                {/* Profile Card */}
                <div className="animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
                    <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 bg-white border border-border-light rounded-2xl p-4 sm:pr-8 shadow-soft">
                        <div className="flex items-center gap-4 sm:gap-6">
                            <img
                                src={mockData.personalInfo.avatar}
                                alt={mockData.personalInfo.name}
                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover"
                            />
                            <div className="text-left">
                                <h2 className="font-serif text-base sm:text-lg text-charcoal-900">{mockData.personalInfo.name}</h2>
                                <p className="text-sm text-charcoal-500">{mockData.personalInfo.location}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 pt-3 sm:pt-0 sm:ml-4 sm:pl-6 border-t sm:border-t-0 sm:border-l border-border-light w-full sm:w-auto justify-center sm:justify-start">
                            {socialLinks.map(({ platform, icon: Icon, url }) => (
                                <a
                                    key={platform}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleSocialClick(platform, url)}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-cream-100 text-charcoal-600 hover:bg-coral-50 hover:text-coral-600 transition-all duration-200"
                                    aria-label={platform}
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
